import React, { useEffect, useMemo } from "react";
import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { NeedHelpButton } from "@/components/need-help-button";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import { GenerateReportWizard } from "./GenerateReportWizard";
import { useGenerateReportFlow } from "./useGenerateReportFlow";
import { reportCheckboxSx } from "./reportPageFieldFocus";
import {
  MarketDataAssetClassRequiredModal,
  MarketDataQuoteVariantModal,
} from "./InstrumentResolveModals";

export default function GenerateReportPage(): React.ReactElement {
  const history = useHistory();
  const shadcnInset = useShadcnInsetChrome();
  const { setActions: setInsetHeaderActions } = useShadcnInsetHeaderActions();
  const subtleText = useColorModeValue("gray.500", "gray.400");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const borderColor = useColorModeValue("gray.200", "var(--border)");

  const flow = useGenerateReportFlow();
  const { editStrategyModal, instrumentResolveModals } = flow;

  const reportsHelpHeaderActions = useMemo(
    () => <NeedHelpButton href="https://help.tesseratrades.com/reports" />,
    []
  );

  useEffect(() => {
    if (!shadcnInset) {
      setInsetHeaderActions(null);
      return;
    }
    setInsetHeaderActions(reportsHelpHeaderActions);
    return () => setInsetHeaderActions(null);
  }, [reportsHelpHeaderActions, setInsetHeaderActions, shadcnInset]);

  return (
    <>
      <GenerateReportWizard
        subtleText={subtleText}
        textColor={textColor}
        borderColor={borderColor}
        onCancel={() => history.push("/reports")}
        {...flow.wizardProps}
      />

      {instrumentResolveModals.assetClassPayload ? (
        <MarketDataAssetClassRequiredModal
          isOpen={instrumentResolveModals.assetClassOpen}
          onClose={instrumentResolveModals.onAssetClassClose}
          payload={instrumentResolveModals.assetClassPayload}
          onConfirm={instrumentResolveModals.onAssetClassConfirm}
        />
      ) : null}
      <MarketDataQuoteVariantModal
        isOpen={instrumentResolveModals.quoteVariantOpen}
        onClose={instrumentResolveModals.onQuoteVariantClose}
        tradeSymbol={instrumentResolveModals.quoteVariantTradeSymbol}
        candidates={instrumentResolveModals.quoteVariantCandidates}
        suggested={instrumentResolveModals.quoteVariantSuggested}
        onConfirm={instrumentResolveModals.onQuoteVariantConfirm}
        saving={instrumentResolveModals.savingQuoteVariant}
      />

      <Modal isOpen={editStrategyModal.isOpen} onClose={editStrategyModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="12px">
          <ModalHeader fontFamily="'Urbanist', sans-serif">Edit strategy</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel color={subtleText} fontSize="xs" textTransform="uppercase">
                  Name
                </FormLabel>
                <Input
                  value={editStrategyModal.name}
                  onChange={(e) => editStrategyModal.onNameChange(e.target.value)}
                  maxLength={editStrategyModal.nameLimit}
                />
                <Text mt={1} fontSize="xs" color={subtleText}>
                  {editStrategyModal.name.length}/{editStrategyModal.nameLimit}
                </Text>
              </FormControl>
              <FormControl isRequired>
                <FormLabel color={subtleText} fontSize="xs" textTransform="uppercase">
                  Description
                </FormLabel>
                <Textarea
                  value={editStrategyModal.description}
                  onChange={(e) => editStrategyModal.onDescriptionChange(e.target.value)}
                  minH="140px"
                  maxLength={editStrategyModal.descriptionLimit}
                />
                <Text mt={1} fontSize="xs" color={subtleText}>
                  {editStrategyModal.description.length}/{editStrategyModal.descriptionLimit}
                </Text>
              </FormControl>
              <Checkbox
                isChecked={editStrategyModal.isArchived}
                onChange={(e) => editStrategyModal.onArchivedChange(e.target.checked)}
                sx={reportCheckboxSx}
              >
                Archive this strategy (hide from the list by default; not selectable when generating new reports)
              </Checkbox>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={editStrategyModal.onClose}>
              Cancel
            </Button>
            <Button
              bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
              color="white"
              _hover={{ opacity: 0.95 }}
              onClick={() => void editStrategyModal.onSave()}
              isLoading={editStrategyModal.saving}
              loadingText="Saving…"
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
